import {
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ChangeEvent,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  AuthApiError,
  MfaRequiredError,
  identifyLogin,
  type LoginMethods,
} from "@/services/auth";
import { ENV } from "@/config/environment";
import { AequiaMark, Button, Icon, Input, ErrorBanner } from "@/components/ui";

type LoginStep = "email" | "password" | "mfa";

export default function LoginPage() {
  const { login, verifyMfaLogin, loginWithPasskey } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: string } | null)?.from ?? "/dashboard";
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [methods, setMethods] = useState<LoginMethods | null>(null);
  const [mfa, setMfa] = useState<string[]>(["", "", "", "", "", ""]);
  const [mfaChallengeToken, setMfaChallengeToken] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mfaRefs = useRef<Array<HTMLInputElement | null>>([]);

  function goToDestination(mustChangePassword: boolean) {
    navigate(mustChangePassword ? "/force-change-password" : from, {
      replace: true,
    });
  }

  async function submitEmail(e?: FormEvent) {
    e?.preventDefault();
    if (loading) return;
    setError(null);
    if (!email) {
      setError("Ingresa tu correo corporativo");
      return;
    }
    setLoading(true);
    try {
      const m = await identifyLogin(email);
      setMethods(m);
      setStep("password");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudo continuar, reintenta",
      );
    } finally {
      setLoading(false);
    }
  }

  async function submitPassword(e?: FormEvent) {
    e?.preventDefault();
    if (loading) return;
    setError(null);
    if (!password) {
      setError("Ingresa tu contraseña");
      return;
    }
    setLoading(true);
    try {
      const user = await login(email, password);
      goToDestination(user.mustChangePassword);
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        setMfaChallengeToken(err.challenge.mfaChallengeToken);
        setStep("mfa");
        setLoading(false);
        return;
      }
      if (err instanceof AuthApiError && err.code === "ACCOUNT_LOCKED") {
        setError(
          err.message ||
            "Cuenta bloqueada temporalmente. Intenta más tarde o contacta a un administrador.",
        );
      } else {
        setError(err instanceof Error ? err.message : "Credenciales inválidas");
      }
    } finally {
      setLoading(false);
    }
  }

  async function passkeyLogin() {
    setError(null);
    setLoading(true);
    try {
      const user = await loginWithPasskey(email);
      goToDestination(user.mustChangePassword);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudo iniciar con passkey",
      );
    } finally {
      setLoading(false);
    }
  }

  function ssoStart() {
    if (methods?.sso.startUrl) {
      window.location.href = `${ENV.backendUrl}${methods.sso.startUrl}`;
    }
  }

  async function submitMfa(e?: FormEvent) {
    e?.preventDefault();
    if (loading) return;
    setError(null);
    const code = useRecoveryCode ? recoveryCode.trim() : mfa.join("");
    if (!useRecoveryCode && code.length < 6) {
      setError("Ingresa los 6 dígitos del código MFA");
      return;
    }
    if (useRecoveryCode && code.length < 8) {
      setError("Ingresa un código de recuperación válido");
      return;
    }
    setLoading(true);
    try {
      const user = await verifyMfaLogin(
        mfaChallengeToken,
        code,
        useRecoveryCode,
      );
      goToDestination(user.mustChangePassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Código MFA inválido");
    } finally {
      setLoading(false);
    }
  }

  function changeEmail() {
    setStep("email");
    setMethods(null);
    setPassword("");
    setError(null);
  }

  function onFormSubmit(e: FormEvent) {
    if (step === "email") return submitEmail(e);
    if (step === "mfa") return submitMfa(e);
    return submitPassword(e);
  }

  function submitOnEnter(e: KeyboardEvent<HTMLInputElement>) {
    if (
      e.key !== "Enter" ||
      e.shiftKey ||
      e.metaKey ||
      e.ctrlKey ||
      e.altKey ||
      e.nativeEvent.isComposing
    ) {
      return;
    }
    e.preventDefault();
    if (!loading) e.currentTarget.form?.requestSubmit();
  }

  function setMfaDigit(i: number, val: string) {
    const v = val.replace(/\D/g, "").slice(-1);
    setMfa((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
    if (v && i < 5) mfaRefs.current[i + 1]?.focus();
  }
  function onMfaKey(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !mfa[i] && i > 0)
      mfaRefs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) mfaRefs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) mfaRefs.current[i + 1]?.focus();
  }
  function onMfaPaste(e: ChangeEvent<HTMLInputElement>) {
    const txt = e.target.value.replace(/\D/g, "").slice(0, 6).split("");
    if (txt.length === 6) {
      setMfa(txt);
      mfaRefs.current[5]?.focus();
    }
  }

  return (
    <div className="paper grid min-h-[100svh] grid-cols-1 grid-rows-[auto_minmax(0,1fr)] md:min-h-screen md:grid-cols-2 md:grid-rows-none">
      {/* Left — editorial brand panel */}
      <div
        className="grain relative flex min-h-0 flex-col gap-5 overflow-hidden border-b border-border p-6 md:min-h-screen md:justify-between md:border-b-0 md:border-r md:p-12"
        style={{
          background:
            "linear-gradient(180deg, rgb(var(--app-bg-2)) 0%, rgb(var(--app-bg)) 100%)",
        }}
      >
        <div>
          <AequiaMark size={52} className="md:hidden" />
          <AequiaMark size={96} className="hidden md:inline-flex" />
        </div>
        <div className="max-w-[520px]">
          <div className="mb-4 text-[11px] font-semibold uppercase tracking-eyebrow text-primary md:mb-6">
            <span className="border-b border-primary pb-1">
              Plataforma SaaS legal · v1.0
            </span>
          </div>
          <h1
            className="font-serif font-medium text-text leading-[1.02] tracking-tighter2 m-0"
            style={{ fontSize: "clamp(30px, 7vw, 64px)" }}
          >
            Acuerdos justos,
            <br />
            <span className="italic text-primary">decisiones</span>{" "}
            inteligentes.
          </h1>
          <p className="mt-5 hidden max-w-[460px] text-base leading-relaxed text-text-2 sm:block">
            Aequia combina edición colaborativa, motor BPMN y una capa de IA con
            RAG empresarial alineada a las posiciones de cada cliente — para
            revisar, redactar y aprobar acuerdos legales sin fricción.
          </p>
          <div className="mt-9 hidden flex-wrap gap-x-7 gap-y-2 text-xs text-muted md:flex">
            <span className="inline-flex items-center gap-2">
              <Icon name="shieldAlt" size={14} className="text-primary" /> ISO
              27001 · GDPR · LGPD
            </span>
            <span className="inline-flex items-center gap-2">
              <Icon name="signature" size={14} className="text-primary" /> eIDAS
              · firma avanzada
            </span>
            <span className="inline-flex items-center gap-2">
              <Icon name="layers" size={14} className="text-primary" />{" "}
              Multi-tenant aislado
            </span>
          </div>
        </div>
        <footer className="mt-auto grid gap-2 border-t border-border pt-4 text-[11px] text-muted md:hidden">
          <span className="inline-flex items-center gap-2">
            <Icon name="shieldAlt" size={14} className="text-primary" /> ISO
            27001 · GDPR · LGPD
          </span>
          <span className="inline-flex items-center gap-2">
            <Icon name="signature" size={14} className="text-primary" /> eIDAS ·
            firma avanzada
          </span>
          <span className="inline-flex items-center gap-2">
            <Icon name="layers" size={14} className="text-primary" />{" "}
            Multi-tenant aislado
          </span>
          <span className="pt-1 text-faint">
            © 2026 Aequia · acuerdos justos.
          </span>
        </footer>
        <footer className="hidden justify-between text-[11px] text-faint md:flex">
          <span>© 2026 Aequia · acuerdos justos.</span>
        </footer>
        <svg
          viewBox="0 0 400 400"
          className="pointer-events-none absolute right-[-100px] top-20 hidden h-[460px] w-[460px] opacity-[0.06] md:block"
        >
          <circle
            cx="200"
            cy="200"
            r="180"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
          <circle
            cx="200"
            cy="200"
            r="140"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
          <circle
            cx="200"
            cy="200"
            r="100"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
          <text
            x="200"
            y="60"
            textAnchor="middle"
            fontFamily="Newsreader"
            fontSize="18"
            fontStyle="italic"
            fill="currentColor"
          >
            aequitas sequitur legem
          </text>
          <text
            x="200"
            y="350"
            textAnchor="middle"
            fontFamily="Newsreader"
            fontSize="14"
            fill="currentColor"
          >
            A · E · Q · U · I · A
          </text>
        </svg>
      </div>

      {/* Right — form */}
      <div className="flex items-start justify-center bg-bg p-6 pt-8 md:items-center md:p-8">
        <form onSubmit={onFormSubmit} className="w-full max-w-[420px]">
          {step === "email" && (
            <>
              <div className="text-[11px] uppercase tracking-eyebrow text-muted mb-2 font-semibold">
                Acceso seguro
              </div>
              <h2 className="font-serif text-3xl font-medium tracking-tightish mb-6">
                Inicie sesión
              </h2>
              <Input
                label="Correo corporativo"
                type="email"
                inputSize="lg"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={submitOnEnter}
                placeholder="usuario@empresa.com"
                autoComplete="username"
                required
              />
              {error && (
                <div className="my-3">
                  <ErrorBanner message={error} />
                </div>
              )}
              <Button
                type="submit"
                variant="brand"
                size="lg"
                full
                loading={loading}
                className="mt-2"
              >
                Continuar
              </Button>
            </>
          )}

          {step === "password" && methods && (
            <>
              <button
                type="button"
                onClick={changeEmail}
                className="text-xs text-muted hover:text-text mb-4 inline-flex items-center gap-1"
              >
                <Icon name="chevronL" size={13} /> {email} · cambiar
              </button>
              <h2 className="font-serif text-3xl font-medium tracking-tightish mb-6">
                Bienvenido de vuelta
              </h2>

              {methods.sso.enabled && (
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  full
                  icon="shieldAlt"
                  onClick={ssoStart}
                  className="mb-2"
                >
                  {methods.sso.buttonLabel || "Acceso corporativo"}
                </Button>
              )}
              {methods.hasPasskey && (
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  full
                  icon="key"
                  onClick={passkeyLogin}
                  loading={loading}
                >
                  Continuar con passkey
                </Button>
              )}

              {(methods.sso.enabled || methods.hasPasskey) && (
                <div className="my-5 flex items-center gap-3 text-[11px] text-muted">
                  <div className="flex-1 border-t border-border" />
                  <span className="uppercase tracking-eyebrow">
                    o con contraseña
                  </span>
                  <div className="flex-1 border-t border-border" />
                </div>
              )}

              <div className="mb-3">
                <label
                  htmlFor="login-password"
                  className="block text-xs font-medium text-muted mb-1.5"
                >
                  Contraseña
                </label>
                <Input
                  id="login-password"
                  type={showPwd ? "text" : "password"}
                  inputSize="lg"
                  hideWrapper
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={submitOnEnter}
                  autoComplete="current-password"
                  suffix={
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="text-muted hover:text-text"
                      aria-label="Mostrar/ocultar contraseña"
                    >
                      <Icon name="eye" size={15} />
                    </button>
                  }
                />
              </div>
              <div className="flex items-center justify-end text-xs text-muted mb-5">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="hover:text-text underline"
                >
                  ¿Olvidó su clave?
                </button>
              </div>
              {error && (
                <div className="mb-3">
                  <ErrorBanner message={error} />
                </div>
              )}
              <Button
                type="submit"
                variant="brand"
                size="lg"
                full
                loading={loading}
              >
                Iniciar sesión
              </Button>
            </>
          )}

          {step === "mfa" && (
            <>
              <div className="text-[11px] uppercase tracking-eyebrow text-muted mb-2 font-semibold">
                Verificación en 2 pasos
              </div>
              <h2 className="font-serif text-3xl font-medium tracking-tightish mb-2">
                Código MFA
              </h2>
              <p className="text-sm text-muted mb-6">
                Ingrese el código de 6 dígitos de su aplicación autenticadora.
              </p>
              {!useRecoveryCode ? (
                <div className="flex gap-2 mb-2">
                  {mfa.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        mfaRefs.current[i] = el;
                      }}
                      inputMode="numeric"
                      maxLength={i === 0 ? 6 : 1}
                      value={d}
                      onChange={(e) =>
                        i === 0 && e.target.value.length === 6
                          ? onMfaPaste(e)
                          : setMfaDigit(i, e.target.value)
                      }
                      onKeyDown={(e) => {
                        submitOnEnter(e);
                        onMfaKey(i, e);
                      }}
                      className="w-[52px] h-[60px] text-center font-serif text-2xl border border-border-strong rounded-lg bg-elevated focus:outline-none focus:border-text focus:shadow-[0_0_0_3px_rgba(20,18,13,0.06)]"
                      aria-label={`Dígito ${i + 1}`}
                    />
                  ))}
                </div>
              ) : (
                <Input
                  label="Código de recuperación"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  onKeyDown={submitOnEnter}
                  inputSize="lg"
                  autoComplete="one-time-code"
                />
              )}
              <p className="text-[11px] text-muted mb-5">
                Este paso se valida contra la política MFA del workspace activo.
              </p>
              {error && (
                <div className="mb-3">
                  <ErrorBanner message={error} />
                </div>
              )}
              <Button
                type="submit"
                variant="brand"
                size="lg"
                full
                loading={loading}
              >
                Verificar
              </Button>
              <button
                type="button"
                onClick={() => {
                  setUseRecoveryCode((v) => !v);
                  setError(null);
                }}
                className="text-xs text-muted hover:text-text mt-3 block w-full text-center"
              >
                {useRecoveryCode
                  ? "Usar código del autenticador"
                  : "Usar código de recuperación"}
              </button>
              <button
                type="button"
                onClick={changeEmail}
                className="text-xs text-muted hover:text-text mt-2 block w-full text-center"
              >
                Volver
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
